'use client';

import 'overlayscrollbars/overlayscrollbars.css';

import React, { useEffect, type ReactNode, useMemo } from 'react';
import cookies from 'js-cookie';

import defaultsDeep from 'lodash.defaultsdeep';
import { NavbarBase, Container, Group } from './Navbar.styled';
import Logo, { type LogoProps } from './Logo';

import Login from './Login';

import NavbarDefault from './Navbar.default';
import type { CooperationProps } from './Cooperation';

import { useNavbar, useToken } from './state';
import useTranslation from '../translation/useTranslation';

import axios, { createStorebrandInterceptor } from '../utils/axios';
import Cooperation from './Cooperation';

export interface NavbarProps {
  logo?: LogoProps;

  show?: {
    login?: boolean;
    notifications?: boolean;
    menu?: boolean;
    logout?: boolean;
  };

  cooperation?: CooperationProps;

  children?: ReactNode;

  login?: () => Promise<unknown>;
  logout?: () => Promise<unknown>;
  getToken?: () => Promise<string>;
}

const defaultProps: NavbarProps = {
  login: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  getToken: () => Promise.resolve(''),

  logo: {
    desktop: {
      src: 'https://assets-t.storebrand.no/logos/beta/storebrand-logo-desktop.svg',
      alt: 'Storebrand Logo',
    },
    mobile: {
      src: 'https://assets-t.storebrand.no/logos/beta/storebrand-logo-only.svg',
      alt: 'Storebrand Logo',
    },
  },

  show: {
    login: true,
    notifications: true,
    menu: true,
    logout: false,
  },

  cooperation: undefined,
  children: undefined,
};

function Navbar({ children, ...props }: NavbarProps) {
  const mergedProps: Required<NavbarProps> = useMemo(
    () => defaultsDeep(props, window.stbauth, defaultProps),
    [props],
  );

  const setNavbarProps = useNavbar((state) => state.setNavbarProps);

  useEffect(() => {
    setNavbarProps(mergedProps);
  }, [mergedProps, setNavbarProps]);

  const {
    login,
    show,
    logo,
    getToken,
    cooperation,
  } = mergedProps;

  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      createStorebrandInterceptor(getToken),
    );

    return () => axios.interceptors.request.eject(interceptor);
  }, [getToken]);

  const token = useToken((state) => state.token);
  const fetchToken = useToken((state) => state.fetchToken);
  const fetchLanguage = useTranslation((state) => state.fetchLanguage);

  useEffect(() => {
    fetchToken();
    fetchLanguage();
  }, [fetchLanguage, fetchToken]);

  useEffect(() => {
    if (token) {
      cookies.set('stb-auth-menu', 'true', {
        domain: '.storebrand.no',
        expires: 1 / 48,
      });
    } else {
      cookies.set('stb-auth-menu', 'false', {
        domain: '.storebrand.no',
      });
    }
  }, [token]);

  if (!token) {
    return (
      <NavbarBase>
        <Container>
          <Logo {...logo} />
          <Group>
            {show?.login && <Login onClick={() => login()} />}
          </Group>
          {cooperation && <Cooperation {...cooperation} /> }
        </Container>
      </NavbarBase>
    );
  }

  return (
    <NavbarBase>
      <Container>
        {children || <NavbarDefault />}
      </Container>
    </NavbarBase>
  );
}

export default Navbar;
